import { type Html, html, View } from 'rune-ts';
import klass from './FaqSection.module.scss';
import { makeMinusIcon, makePlusIcon } from '../../../../../shared/components/atoms/Icon/landing_icons';
import { isNull, map, pipe, throwIf, toArray } from '@fxts/core';
import { font } from '@services/MarppleShop/shared/typography';

interface QuestionCardProps {
  question: string;
  answer_body: Html;
}

class QuestionCard extends View<QuestionCardProps> {
  override template({ question, answer_body }: QuestionCardProps): Html {
    return html`
      <div class="${klass.question_item}">
        <div class="${klass.question_title_wrapper}">
          <div class="${font('24_bold', '14_bold')}">${question}</div>
          <div class="${klass.toggle_status}">
            ${makePlusIcon({ klass: klass.closed })} ${makeMinusIcon({ klass: klass.opened })}
          </div>
        </div>
        <div class="${klass.answer_body} ${font('16_medium', '14_medium')}">
          <div class="${klass.answer_wrapper}">${answer_body}</div>
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.delegate('click', `.${klass.question_title_wrapper}`, () => {
      this.element().classList.toggle(`${klass.is_open}`);
      const answer_body = pipe(
        this.element().querySelector<HTMLElement>(`.${klass.answer_body}`),
        throwIf(isNull),
      );
      const answer_wrapper = pipe(
        this.element().querySelector<HTMLElement>(`.${klass.answer_wrapper}`),
        throwIf(isNull),
      );
      answer_body.style.height = this.element().matches(`.${klass.is_open}`)
        ? `${answer_wrapper.clientHeight}px`
        : '0px';
    });
  }
}

interface FaqSectionData {
  is_mobile: boolean;
}

export class FaqSection extends View<FaqSectionData> {
  faq_data: QuestionCardProps[];

  constructor(data: FaqSectionData) {
    super(data);
    this.faq_data = [
      {
        question: '마플샵 굿즈 파트너스는 무엇인가요?',
        answer_body: html`
          <p>
            ${data.is_mobile
              ? '크리에이터가 콘텐츠 제작에만 집중할 수 있도록, 팬들이 원하는 굿즈 기획부터 디자인, 제작, 운영, 판매까지 전 과정을 마플샵이 전담 지원하는 파트너 프로그램입니다.'
              : html`크리에이터가 콘텐츠 제작에만 집중할 수 있도록, 팬들이 원하는 굿즈 기획부터 <br />
                  디자인, 제작, 운영, 판매까지 전 과정을 마플샵이 전담 지원하는 파트너 프로그램입니다.`}
          </p>
        `,
      },
      {
        question: '신청 자격과 선정 기준은 어떻게 되나요?',
        answer_body: html`
          <p>크리에이터라면 마플샵 이용 여부와 관계없이 누구나 신청할 수 있습니다.</p>
          <p>
            내부 심사 기준에 따라 선정된 분들께는 개별 이메일 및 유선 연락을 통해 안내드릴 예정입니다.<br />
            미선정자에게 별도 안내가 되지 않는 점 참고 부탁드립니다.
          </p>
        `,
      },
      {
        question: '어떤 상품을 만들 수 있나요?',
        answer_body: html`
          <p>
            마플샵이 보유한 POD 시스템으로 1,400여 종의 굿즈 제작이 가능합니다.<br />
            이 외에도 팬분들을 위한 특정 컨셉에 맞춘 희망 상품이 있다면 커스텀 제작도 지원됩니다.
          </p>
        `,
      },
      {
        question: '저는 해외 팬들이 많은데, 해외 배송도 가능한가요?',
        answer_body: html`
          <p>
            마플샵은 전 세계 110개국 이상으로 해외 배송이 가능합니다. 글로벌 팬분들도 안전하게 굿즈 상품을
            받아보실 수 있습니다.
          </p>
        `,
      },
      {
        question: '굿즈를 만들고 싶긴 한데 다른 지원도 필요해요. 그 외 도움도 주실 수 있나요?',
        answer_body: html`
          <p>
            마케팅 지원 포함 오프라인 팝업스토어, 펀딩, 가챠샵 상품 제작, 옥외광고 등 다양한 혜택이
            제공됩니다.<br />
            신청페이지에 기재되지 않은 항목이라도 필요하신 부분이 있다면 자유롭게 논의할 수 있습니다.
          </p>
        `,
      },
      {
        question: '더 궁금한 점은 어디로 연락하면 되나요?',
        answer_body: html`
          <p>
            추가로 궁금한 사항이 있으시다면 언제든지
            <a href="mailto:creators@marppleshop.com" class="${klass.mail}">creators@marppleshop.com</a> 으로
            편하게 문의 주세요.
          </p>
        `,
      },
    ];
  }

  override template() {
    return html`
      <section class="${klass.container}">
        <div class="${klass.title} ${font('48_bold', '24_bold')}">마플샵 굿즈 파트너스 FAQ</div>
        <div class="${klass.question_list}">${this._renderQuestionCardList()}</div>
      </section>
    `;
  }

  private _renderQuestionCardList() {
    return pipe(
      this.faq_data,
      map((faq) => new QuestionCard(faq)),
      toArray,
    );
  }
}
